import { InvoiceRow, InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";

/**
 * `Created` is the only draft state — an invoice the payer has never seen — so
 * it is the only one that can be removed outright.
 */
const DELETABLE_STATUSES: InvoiceStatus[] = [InvoiceStatus.CREATED];

/**
 * Already visible to the payer, so removal has to be recorded as a
 * cancellation (a `void`) instead of erasing the record. `Pending` belongs here
 * rather than with the drafts: it is a sent invoice awaiting payee review, and
 * the row menu offers it as "Cancel invoice".
 */
const CANCELABLE_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.OPENED,
  InvoiceStatus.OVERDUE,
  InvoiceStatus.PENDING
];

/** Removing an invoice is either a void or an outright delete, never both. */
export type InvoiceRemovalAction = "cancel" | "delete";

/**
 * Which removal operation an invoice's status supports, if any — the single
 * source of truth for the row menu (which item to offer: "Remove" vs "Cancel
 * invoice"). The resolved value is handed to `ModalDelete` as a prop, so the
 * label the user clicked and the mutation that runs cannot disagree: they come
 * from one call. They used to keep separate status lists and drifted — a
 * `Pending` invoice was offered as "Cancel invoice" but then deleted (PLA-3536).
 *
 * `undefined` means neither operation applies — including when the row carries
 * no status at all, so no mutating action fires on an indeterminate state.
 */
export const selectorInvoiceRemovalAction = (
  invoice: InvoiceRow
): InvoiceRemovalAction | undefined => {
  const status = invoice.status;

  if (!status) {
    return undefined;
  }

  if (DELETABLE_STATUSES.includes(status)) {
    return "delete";
  }

  if (CANCELABLE_STATUSES.includes(status)) {
    return "cancel";
  }

  return undefined;
};
